import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, File, Folder, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../../../components/ui/collapsible";
import type { FolderInfo } from "./EvidenceUploadModal";
import type { SourceRow } from "../../../../../models/audit/auditSession";
import { cn } from "../../../../../lib/utils";

interface FolderMappingRowProps {
  folder: FolderInfo;
  samples: SourceRow[];
  selectedSampleId: string | null;
  confidence?: number;
  /**
   * What the match was based on, e.g. "Requester name: J. Smith". A percentage
   * alone gives an auditor nothing to check; this is the reasoning.
   */
  matchedOn?: string | null;
  samplesLoading?: boolean;
  onMappingChange: (sourceRowId: string | null) => void;
  disabled?: boolean;
}

const NO_MATCH_VALUE = "__no_match__";

export function FolderMappingRow({
  folder,
  samples,
  selectedSampleId,
  confidence,
  matchedOn,
  samplesLoading,
  onMappingChange,
  disabled,
}: FolderMappingRowProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Just the sample number. This appended a value out of `data` to make the
  // option "meaningful", but `data` is keyed by attribute uuid, so the lookups
  // it tried ("name", "id", ...) never matched and it fell through to whatever
  // sorted first in JSONB key order. The row already shows the folder and the
  // matched-on field, so the option only has to say which sample it is.
  const getSampleLabel = useMemo(() => {
    return (sample: SourceRow): string =>
      sample.sampleNumber !== null
        ? `Sample ${sample.sampleNumber}`
        : sample.id.slice(0, 8);
  }, []);

  const confidencePercent =
    confidence !== undefined ? Math.round(confidence * 100) : null;
  const isMatched = selectedSampleId !== null;

  const handleValueChange = (value: string) => {
    if (value === NO_MATCH_VALUE) {
      onMappingChange(null);
    } else {
      onMappingChange(value);
    }
  };

  return (
    <Collapsible
      data-testid="zip-folder-row"
      open={isOpen}
      onOpenChange={setIsOpen}
      // Shading belongs to the whole row. On the inner grid it covered only
      // the first 64px, leaving the rest of an unmatched row white.
      className={cn(!isMatched && "bg-stone-50")}
    >
      <div className="grid grid-cols-[1fr_280px] gap-4 px-4 py-3 items-center">
        {/* Folder info with expand toggle */}
        <CollapsibleTrigger className="flex items-center gap-2 text-left hover:bg-stone-100 -ml-2 pl-2 pr-2 py-1 rounded cursor-pointer">
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-stone-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-stone-500 flex-shrink-0" />
          )}
          <Folder className="h-4 w-4 text-stone-500 flex-shrink-0" />
          <span className="font-medium text-stone-900 truncate">
            {folder.name}
          </span>
          <span className="text-sm text-stone-500 flex-shrink-0">
            ({folder.files.length} file{folder.files.length !== 1 ? "s" : ""})
          </span>
        </CollapsibleTrigger>

        {/* What the match was based on, under the folder name: the sample
            column is sized for a select and a badge, and this is prose. Kept
            inside the row's own grid cell so the row reads as one block. */}
        {isMatched && matchedOn && !samplesLoading && (
          <p
            className="col-start-1 row-start-2 text-xs text-stone-500 truncate pl-8 -mt-1"
            title={matchedOn}
          >
            matched on {matchedOn}
          </p>
        )}

        {/* Sample dropdown with confidence */}
        <div className="flex items-center gap-2">
          <Select
            value={selectedSampleId ?? NO_MATCH_VALUE}
            onValueChange={handleValueChange}
            disabled={disabled || samplesLoading}
          >
            <SelectTrigger
              className={cn("flex-1", !isMatched && "text-stone-400 italic")}
            >
              {samplesLoading ? (
                <span className="flex items-center gap-2 text-stone-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading samples...
                </span>
              ) : (
                <SelectValue placeholder="Select sample..." />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value={NO_MATCH_VALUE}
                className="text-stone-400 italic"
              >
                No match
              </SelectItem>
              {samples.map((sample) => (
                <SelectItem key={sample.id} value={sample.id}>
                  {getSampleLabel(sample)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Confidence indicator. Shown for unmatched folders too: detection
              reports a figure either way, and hiding it left the column ragged
              and made a deliberate "no match" look like a missing answer.
              Muted rather than red when there is no sample - a low score on a
              folder that is not a sample is the right answer, not a problem. */}
          {confidencePercent !== null && (
            <span
              className={cn(
                "text-xs whitespace-nowrap",
                !isMatched
                  ? "text-stone-400"
                  : confidencePercent >= 90
                    ? "text-green-600"
                    : confidencePercent >= 70
                      ? "text-amber-600"
                      : "text-red-600",
              )}
            >
              {confidencePercent}% match
            </span>
          )}
        </div>
      </div>

      {/* Expanded file list */}
      <CollapsibleContent>
        <div className="pl-12 pr-4 pb-3 space-y-1">
          {folder.files.map((file) => (
            <div
              key={file.path}
              className="flex items-center gap-2 text-sm text-stone-600 py-1"
            >
              <File className="h-3.5 w-3.5 text-stone-400 flex-shrink-0" />
              <span className="truncate">{file.name}</span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
